import { getCachedSegmentPsd } from '../../../shared/spectral';
import { computeLfHfRatioFromPsd } from '../../../shared/spectral/integrateBandPower';
import type { SpectralWorkspaceSession } from '../types';
import { computeSegmentHrvMetricsFromEvents } from './computeSessionHrvMetrics';
import { getTrendSignalValue } from './featureValue';
import {
  filterSessionEventsForSegment,
  getSessionAvailableSeconds,
} from './resolveSpectraSegment';

export type SessionFullTractMetrics = {
  sessionId: number;
  meanHr: number | null;
  meanRrMs: number | null;
  lfHfRatio: number | null;
  rmssdMs: number | null;
};

const meanOf = (values: number[]) =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

export const computeSessionFullTractMetrics = (
  session: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>
): SessionFullTractMetrics => {
  const events = session.events ?? [];

  const hrValues = events
    .map(event => getTrendSignalValue(event, 'HR'))
    .filter(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value)
    );

  const hrvMetrics = computeSegmentHrvMetricsFromEvents(events);
  const availableSeconds = getSessionAvailableSeconds(events);
  const lfHfRatio =
    availableSeconds.length >= 2
      ? (() => {
          const startSecond = availableSeconds[0];
          const endSecond = availableSeconds[availableSeconds.length - 1];
          const segmentEvents = filterSessionEventsForSegment(
            events,
            startSecond,
            endSecond
          );
          const psdResult = getCachedSegmentPsd(
            session.sessionId,
            startSecond,
            endSecond,
            segmentEvents
          );
          return psdResult.ok ? computeLfHfRatioFromPsd(psdResult.psd) : null;
        })()
      : null;

  return {
    sessionId: session.sessionId,
    meanHr: meanOf(hrValues),
    meanRrMs: hrvMetrics?.meanRrMs ?? null,
    lfHfRatio,
    rmssdMs: hrvMetrics?.rmssdMs ?? null,
  };
};

export const computeSessionsFullTractMetrics = (
  sessions: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>[]
): SessionFullTractMetrics[] =>
  sessions.map(session => computeSessionFullTractMetrics(session));
